import { Match, mergeProps, Show, Switch, useContext } from 'solid-js';
import { Link as RouterLink } from '@solidjs/router';
import clsx from 'clsx';
import { CustomLinkProps } from './link-types';
import { ArrowLeftIcon } from '../../components/icons-library/arrow-left';
import { ArrowUpIcon } from '../../components/icons-library/arrow-up';
import { ArrowRightIcon } from '../../components/icons-library/arrow-right';
import { ArrowDownIcon } from '../../components/icons-library/arrow-down';
import theme from '../../style/theme';
import { navigateToAnchor } from '../../tools/scroll-to-element';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import { isExternalLink } from '../../tools/check-is-external-url';
import removeAmpersand from '../../tools/remove-ampersand';
import { isDownloadLink } from '../../tools/check-is-download-link';

const defaultProps = {
    opensInNewTab: false,
    darkMode: false,
    noBlockSpacing: false,
};

const sizeClasses = {
    small: 'text-[1.3rem]',
    medium: 'text-[1.6rem]',
    large: 'text-[1.8rem]',
};

// Link styles live in @layer components in tailwind.css. Dynamic props
// (size, bold, darkMode, noBlockSpacing, labelColor, arrowColor) remain
// inline — the CSS classes handle only the static baseline.

// Helper to get line limit styles
const getLineLimitStyles = (lineLimit?: number) => {
    if (!lineLimit) return {};
    return {
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-box-orient': 'vertical',
        '-webkit-line-clamp': String(lineLimit),
    };
};

export const Link = (componentProps: CustomLinkProps) => {
    const { viewport, siteInfo } = useContext(AppContext);

    const props = mergeProps(defaultProps, componentProps);

    let url = removeTrailingSlash(props.url);
    url = removeAmpersand(url);

    if ((!props.asButton && !url) || !props.label) {
        return <></>;
    }

    const isSmallScreen = () => viewport.width <= theme.breakpoints.TABLET;

    const opensInNewTab = () => props.opensInNewTab || isExternalLink(url) || isDownloadLink(url);

    const anchorLink = () => {
        return props.anchorLink || (url?.slice(0, 1) === '#' ? url.replace('#', '') : '');
    };

    // Get classes for standard link
    const getLinkClasses = () => clsx(
        'link-standard',
        sizeClasses[props.size || 'medium'],
        !props.noBlockSpacing && 'my-[1.43rem]',
        props.darkMode && ['text-cvi-fg-inverse', 'hover:text-cvi-fg-on-color-muted'],
    );

    // Arrow link classes. DS component/button/link has fixed typography
    // (label-md: 16px/24px/medium) — `size` and `bold` props don't apply; the
    // CSS class provides DS-spec values directly.
    const getArrowLinkClasses = () => 'link-arrow';

    // Arrow link label classes
    const getArrowLinkLabelClasses = () => clsx(
        props.breakWords && ['w-full', 'break-words'],
    );

    const getArrowIconClasses = () => clsx(
        'link-arrow-icon',
        !props.arrowDirection && 'hidden',
    );

    const getButtonClasses = () => clsx(
        'link-as-button',
        !props.noBlockSpacing && 'my-[1.43rem]',
    );

    // Get inline styles for color and line limit
    const getInlineStyles = () => {
        const styles: Record<string, string> = {};

        // Line limit
        if (props.lineLimit) {
            Object.assign(styles, getLineLimitStyles(props.lineLimit));
        }

        return styles;
    };

    // Get arrow link inline styles (only applied when consumer passes
    // labelColor explicitly). Without an explicit labelColor, the
    // .link-arrow class color (--component-button-color-link-default-text-color,
    // DS fg-link #00788A) wins.
    const getArrowLinkStyles = () => {
        const styles: Record<string, string> = {};

        if (props.labelColor) {
            styles.color = props.labelColor;
        }

        if (props.lineLimit) {
            Object.assign(styles, getLineLimitStyles(props.lineLimit));
        }

        return styles;
    };

    const arrowIconProps = () => ({
        fill: props.arrowColor || 'currentColor',
        width: 'var(--component-button-link-md-icon-size)',
        height: 'var(--component-button-link-md-icon-size)',
    });

    const arrowLinkContent = () => {
        return (
            <>
                <Show when={props.arrowPosition === 'right'}>
                    <span class={getArrowLinkLabelClasses()}>
                        {removeAmpersand(props.label)}
                    </span>
                </Show>
                <span class={getArrowIconClasses()}>
                    <Show
                        when={props.customIcon}
                        fallback={
                            <Switch>
                                <Match when={props.arrowDirection == 'up'}>
                                    <ArrowUpIcon {...arrowIconProps()} />
                                </Match>
                                <Match when={props.arrowDirection == 'right'}>
                                    <ArrowRightIcon {...arrowIconProps()} />
                                </Match>
                                <Match when={props.arrowDirection == 'down'}>
                                    <ArrowDownIcon {...arrowIconProps()} />
                                </Match>
                                <Match when={props.arrowDirection == 'left'}>
                                    <ArrowLeftIcon {...arrowIconProps()} />
                                </Match>
                            </Switch>
                        }
                    >
                        {props.customIcon}
                    </Show>
                </span>
                <Show when={props.arrowPosition === 'left'}>
                    <span class={getArrowLinkLabelClasses()}>
                        {removeAmpersand(props.label)}
                    </span>
                </Show>
                <Show when={!props.arrowPosition}>
                    <span class={clsx(props.breakWords && ['w-full', 'break-words'])}>
                        {removeAmpersand(props.label)}
                    </span>
                </Show>
            </>
        );
    };

    return (
        <ErrorCatcher componentName='Link'>
            {/* Standard Link (not asButton, not plainStyle) */}
            <Show when={!props.asButton && !props.plainStyle && url}>
                {/* Anchor Link */}
                <Show when={anchorLink()}>
                    <a
                        href={url}
                        class={getLinkClasses()}
                        style={getInlineStyles()}
                        onClick={(e: any) => navigateToAnchor({
                            event: e,
                            id: anchorLink(),
                            smallScreen: isSmallScreen(),
                            isCareSite: siteInfo.siteType === 'atos-care',
                        })}
                        ref={(el: HTMLElement) => props.element && props.element(el)}
                    >
                        {removeAmpersand(props.label)}
                    </a>
                </Show>
                {/* Regular Link */}
                <Show when={!anchorLink()}>
                    <RouterLink
                        href={url}
                        class={getLinkClasses()}
                        style={getInlineStyles()}
                        onClick={props.onClick}
                        ref={(el: HTMLElement) => props.element && props.element(el)}
                        {...(opensInNewTab()
                            ? {
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            }
                            : {})}
                    >
                        {removeAmpersand(props.label)}
                    </RouterLink>
                </Show>
            </Show>

            {/* Arrow/Plain Style Link */}
            <Show when={props.plainStyle && url}>
                {/* Anchor Arrow Link */}
                <Show when={anchorLink()}>
                    <a
                        href={url}
                        class={getArrowLinkClasses()}
                        style={getArrowLinkStyles()}
                        onClick={(e: any) => navigateToAnchor({
                            event: e,
                            id: anchorLink(),
                            smallScreen: isSmallScreen(),
                            isCareSite: siteInfo.siteType === 'atos-care',
                        })}
                        ref={(el: HTMLElement) => props.element && props.element(el)}
                    >
                        {arrowLinkContent()}
                    </a>
                </Show>
                {/* Regular Arrow Link */}
                <Show when={!anchorLink()}>
                    <RouterLink
                        href={url}
                        class={getArrowLinkClasses()}
                        style={getArrowLinkStyles()}
                        ref={(el: HTMLElement) => props.element && props.element(el)}
                        {...(opensInNewTab()
                            ? {
                                target: '_blank',
                                rel: 'noopener noreferrer',
                            }
                            : {})}
                    >
                        {arrowLinkContent()}
                    </RouterLink>
                </Show>
            </Show>

            {/* Button Style Link — arrow variant (plainStyle + asButton) */}
            <Show when={props.asButton && props.plainStyle}>
                <button
                    type="button"
                    class={getArrowLinkClasses()}
                    style={getArrowLinkStyles()}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                >
                    {arrowLinkContent()}
                </button>
            </Show>

            {/* Button Style Link */}
            <Show when={props.asButton && !props.plainStyle}>
                <button
                    class={getButtonClasses()}
                    style={{
                        ...getInlineStyles(),
                        ...(props.customCss ? {} : {}), // customCss would need to be parsed
                    }}
                    onClick={props.onClick}
                    ref={(el: HTMLElement) => props.element && props.element(el)}
                >
                    {removeAmpersand(props.label)}
                </button>
            </Show>
        </ErrorCatcher>
    );
};

Link.parseProps = (atts: any) => {
    let anchorLink = '';
    if (atts?.url?.slice(0, 1) === '#') {
        anchorLink = atts.url.replace('#', '');
    }

    return {
        ...atts,
        anchorLink,
    };
};

export default Link;
